/**
 * Public Certificate Verification Service
 * This service handles public certificate verification API calls without authentication
 */

import axios from 'axios';
import { getApiBaseUrl } from '@/config/api.config';

// Types for the public certificate verification response
export interface PublicCertificateHolder {
  holder_name: string;
  masked_email: string;
}

export interface PublicCertificationInfo {
  // Fields may vary by certificate type (certification vs course vs learning_path)
  certification_id?: string;
  certification_name?: string;
  // Generic fields used for course/learning_path certificates
  certificate_type?: string;
  item_id?: string;
  item_name?: string;
  // Common fields
  description?: string;
  targeted_audience?: string;
  outcomes?: string[];
  tags?: string[];
  banner_image_url?: string;
  thumbnail_image_url?: string;
  total_certified?: number;
}

export interface PublicCertificateVerificationResponse {
  is_valid: boolean;
  message: string;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
  certificate_number: string;
  tier?: string;
  holder: PublicCertificateHolder;
  certification: PublicCertificationInfo;
  issued_date: string;
  expiry_date?: string;
  percentage_achieved?: number;
  passing_percentage?: number;
  pdf_url?: string;
  preview_image_url?: string;
  verification_url?: string;
  logo_url?: string;
  linkedin_organization_id?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  result?: T;
  data?: T;  // Backend may return "data" instead of "result"
  code?: number;
  error?: {
    error: string;
    error_description: string;
  };
}

// Create a separate axios instance without auth interceptors for public endpoints
const publicApiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * Verify a certificate by its certificate number (public endpoint)
 * Backend endpoint: /public/certificates/verify/:certificate_number
 * @param certificateNumber - The unique certificate number
 * @returns Certificate verification details
 */
export const verifyCertificate = async (
  certificateNumber: string
): Promise<PublicCertificateVerificationResponse> => {
  try {
    // Backend uses unified endpoint: /public/certificates/verify/{certificate_number}
    const endpoint = `/public/certificates/verify/${encodeURIComponent(certificateNumber)}`;
    const response = await publicApiClient.get<ApiResponse<PublicCertificateVerificationResponse>>(endpoint);

    // Backend may return data in "result" or "data" field
    const certificateData = response.data.result || response.data.data;

    if (response.data.success && certificateData) {
      return certificateData;
    }

    throw new Error(
      response.data.error?.error_description || 'Certificate not found'
    );
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Certificate not found. Please check the certificate number.');
    }
    if (error.response?.data?.error?.error_description) {
      throw new Error(error.response.data.error.error_description);
    }
    throw error;
  }
};

export default {
  verifyCertificate,
};
